import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import Typography from 'src/components/Typography';
import downloadFile from 'src/utils/downloadFile';
import {
    addFile,
    deleteFile,
    importClaimsFetch,
    clearClaimsImport,
} from 'src/resources/claimsImport/reducer';
import { FileImportType } from 'src/types/claimsImport';
import { FileType } from 'src/types/file';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { WarningIcon } from 'src/components/SkladonIcon/SmallIcons';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';
import Tooltip from 'src/components/Tooltip';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import ValidationTable from './ValidationTable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import ColumnOptions from './ColumnOptions';

const WarningButton = styled(IconButton)<IconButtonProps>({
    '& svg path': {
        fill: colors.primary_O,
    },
});

const findFile = (files: FileImportType[], inputFile: FileType) => {
    return files.find((file) => file.file.name === inputFile.name);
};

const ClaimsImportContainer: FC = () => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);

    const loading = useSelector((state: RootStoreType) => state.claimsImport.loading);
    const files = useSelector((state: RootStoreType) => state.claimsImport.files);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const claimStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimStatusCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);

    const permissionImport = useCheckPermission('CLAIM.IMPORT');
    const disabled = !permissionImport;
    const disableEdit = disabled || !files.filter((file) => !file.imported).length;

    const backUrl = getBackUrl(locationHistory, '/claims/');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('claimsImport.title'), backUrl }));
    }, [backUrl, dispatch, locationHistory]);

    useEffect(() => {
        return () => {
            dispatch(clearClaimsImport());
        };
    }, [dispatch]);

    let filesLoading = false;
    let filesError = false;

    const inputFiles = files.map((file) => {
        if (file.loading) {
            filesLoading = true;
        }
        if (file.validation?.some((row) => row.validationErrors?.length)) {
            filesError = true;
        }
        if (file.error) {
            filesError = true;
        }
        return file.file;
    });

    const customFileActions: CustomFileActions = (inputFile) => {
        const file = findFile(files, inputFile);
        if (!file) {
            return null;
        }

        const hasValidationErrors = file.validation?.some((row) => row.validationErrors?.length);

        return (
            <>
                {file.loading && (
                    <IconButton disabled>
                        <CircularProgress color="secondary" size={20} />
                    </IconButton>
                )}
                {hasValidationErrors && (
                    <Tooltip title={translate('claimsImport.incorrectFileData')}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {file.error && (
                    <Tooltip title={file.error}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {!hasValidationErrors && !file.error && !file.loading && (
                    <IconButton disabled>
                        <TaskAltIcon style={{ fill: 'green' }} />
                    </IconButton>
                )}
            </>
        );
    };

    const downloadExcelPattern = () => {
        downloadFile('/xlsx/import_claims.xlsx', 'import_claims.xlsx');
    };

    const onUpdate = (uploadedFiles: FilesPropType) => {
        uploadedFiles.forEach((file) => loadFileData(file as File));
    };

    const onDelete = (file: FileType) => {
        dispatch(deleteFile(file));
    };

    const handleImport = () => {
        setConfirmParams({
            title: translate('claimsImport.action.import'),
            content: translate('claimsImport.importConfirm'),
            action: () => dispatch(importClaimsFetch()),
        });
    };

    const loadFileData = (file: File) => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            dispatch(
                addFile({
                    data: typeof reader.result === 'string' ? reader.result : '',
                    name: file.name,
                    size: file.size,
                    type: file.type,
                })
            );
        };
    };

    return (
        <>
            <TopBlock
                disabled={disabled}
                disableEdit={disableEdit}
                filesError={filesError}
                handleImport={handleImport}
            />
            <form ref={formRef}>
                {(loading || filesLoading) && <Loading />}
                <Typography component="div" gutterBottom>
                    <span onClick={downloadExcelPattern} style={{ cursor: 'pointer' }}>
                        {translate('claimsImport.excelFilePattern')}
                        <IconButton color="primary">
                            <DownloadIcon />
                        </IconButton>
                    </span>
                    <Tooltip title={translate('claimsImport.columnsOptions')}>
                        <IconButton onClick={() => setShowColumnOptions((value) => !value)}>
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <FileUpload
                    files={inputFiles}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    accept=".xls, .xlsx"
                    height={150}
                    multiple
                    disabled={disabled}
                    rawFiles
                    displayFilesList
                    disableShowFile
                    disableDowloadFile
                    customFileActions={customFileActions}
                />
                <ValidationTable
                    claimStatusCodes={claimStatusCodes}
                    currencyCodes={currencyCodes}
                    files={files}
                />

                {showColumnOptions && (
                    <ColumnOptions
                        claimStatusCodes={claimStatusCodes}
                        currencyCodes={currencyCodes}
                        onClose={() => setShowColumnOptions(false)}
                    />
                )}

                {confirmParams && (
                    <ConfirmDialog
                        title={confirmParams.title}
                        content={confirmParams.content}
                        action={confirmParams.action}
                        onClose={() => setConfirmParams(null)}
                    />
                )}
            </form>
        </>
    );
};

export default ClaimsImportContainer;
